import { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

import { useOnClickOutside } from 'utils/onClickOutside'
import { frozenVaults, VaultDisplayInfo } from 'utils/vaultDisplayInfo'
import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { Menu } from 'components/Menu/Menu'
import { Typography } from 'components/Typography/Typography'
import { ROUTES } from 'utils/route-constants'
import { round } from '@kunalabs-io/kai'
import { useVaultInfo } from '@/shared/kai/hooks/useVaultInfo'
import { IconInfo } from 'components/Icons/InfoIcon'
import { useIsMobile } from 'utils/useIsMobile'

const TokenDetails = styled.div`
  // margin: 20px 0;
  display: flex;
  align-items: center;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding-top: 0;
    padding-bottom: 20px;
    flex-direction: column;
    justify-content: flex-start;
  }
`

const StyledMenuItem = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  text-decoration: none;
  justify-content: space-between;
  padding: 16px;
  ${p => p.$isActive && `background-color: ${p.theme.colors.background.hover};`}
  &:hover {
    background-color: ${p => p.theme.colors.background.hover};
  }
`

const WhatIsSingleAsset = styled(Typography)`
  cursor: pointer;
  & > div {
    display: flex;
    align-items: center;
    cursor: pointer;
  }
  & > div > svg {
    margin-right: 8px;
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
  }

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    display: flex;
    align-items: center;
  }
`

interface Props {
  displayInfo: VaultDisplayInfo
  isDontShowSelected: boolean
  onResetDontShowThisAgain: () => void
  displayInfoList: VaultDisplayInfo[]
  handleNewPositionClick?: () => void
}

interface MenuItemProps {
  item: VaultDisplayInfo
  toggleMenu: () => void
  isActive: boolean
  isFrozenVault: boolean
}

const MenuItem = ({ item, toggleMenu, isActive, isFrozenVault }: MenuItemProps) => {
  const vaultInfo = useVaultInfo(item.vault, { refetchInterval: 5000 })
  const estApy = vaultInfo.apy !== undefined ? round(vaultInfo.apy * 100, 2) + '%' : ''

  return (
    <StyledMenuItem
      key={item.tSymbol}
      to={`/${ROUTES.VAULTS}${isFrozenVault ? '/frozen' : ''}/${item.tSymbol}`}
      onClick={toggleMenu}
      $isActive={isActive}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <item.icon />
        <Typography variant="label2" fontWeight="medium" style={{ marginLeft: 8 }}>
          {`${item.tSymbol} ${isFrozenVault ? '(paused)' : ''}`}
        </Typography>
      </div>
      <Typography variant="label2" fontWeight="medium">
        {estApy}
      </Typography>
    </StyledMenuItem>
  )
}

export const VaultsMenu = ({ displayInfo, isDontShowSelected, onResetDontShowThisAgain, displayInfoList }: Props) => {
  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuNode = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutside(menuNode, onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  const isMobile = useIsMobile()

  const isFrozenActiveVault = frozenVaults.some(frozenVault => frozenVault.vault.id === displayInfo.vault.id)
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <TokenDetails>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <displayInfo.icon width={isMobile ? 24 : 40} height={isMobile ? 24 : 40} />
          <div
            style={{ marginLeft: 16, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={toggleMenu}
          >
            <Typography
              variant={isMobile ? 'description' : 'subtitle1'}
              fontWeight="bold"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span style={{ marginRight: isFrozenActiveVault ? 8 : 16 }}>{displayInfo.tSymbol}</span>
              {isFrozenActiveVault ? (
                <span style={{ fontSize: 16, fontWeight: 400, marginRight: 16 }}>{'(paused)'}</span>
              ) : (
                ''
              )}
              <IconArrowDown width={isMobile ? 16 : 32} height={isMobile ? 16 : 32} />
            </Typography>
          </div>
        </div>
        {isDontShowSelected && (
          <div style={isMobile ? { marginTop: 8 } : { marginLeft: 'auto' }}>
            <WhatIsSingleAsset variant="label1" fontWeight="light">
              <div onClick={onResetDontShowThisAgain}>
                <IconInfo />
                What are Single Asset Vaults
              </div>
            </WhatIsSingleAsset>
          </div>
        )}
      </TokenDetails>
      {isMenuOpen && (
        <Menu anchorElement={menuNode} className="mobile" style={{ top: 80, left: 0, padding: '8px 0', zIndex: 1 }}>
          {displayInfoList.map(d => {
            const isFrozenVault = frozenVaults.some(frozenVault => frozenVault.vault.id === d.vault.id)

            return (
              <MenuItem
                key={d.vault.id}
                item={d}
                toggleMenu={toggleMenu}
                isFrozenVault={isFrozenVault}
                isActive={displayInfo.vault.id === d.vault.id}
              />
            )
          })}
        </Menu>
      )}
    </div>
  )
}
